import { ComponentRef, Type, reflectComponentType } from '@angular/core';

/**
 * Per-`componentType` cache of the public input names exposed by a dynamic
 * component. Calling `reflectComponentType` every assignment is wasteful
 * because the result is stable for the lifetime of the application.
 */
const _inputNameCache = new WeakMap<Type<unknown>, Set<string>>();

function getInputNames(componentType: Type<unknown>): Set<string> {
    let names = _inputNameCache.get(componentType);
    if (names) {
        return names;
    }

    const mirror = reflectComponentType(componentType);
    names = new Set(mirror?.inputs.map((input) => input.propName) ?? []);
    _inputNameCache.set(componentType, names);
    return names;
}

/**
 * Assigns a map of `name → value` pairs to a dynamically created component.
 *
 * Supports both styles in a single call:
 *  - Signal inputs (`config = input<…>()`) are written via
 *    `ComponentRef.setInput()` so the framework runs change detection.
 *  - Plain decorator inputs (`@Input() config?: IFoo`) are written directly
 *    on the instance, since `setInput()` would throw for them.
 *
 * The reflection cost is paid once per `componentType` thanks to
 * `_inputNameCache`.
 */
export function setComponentInputs(
    componentRef: ComponentRef<unknown>,
    values: Record<string, unknown>,
): void {
    const inputNames = getInputNames(componentRef.componentType);
    const instance = componentRef.instance as Record<string, unknown>;

    for (const [name, value] of Object.entries(values)) {
        if (inputNames.has(name)) {
            componentRef.setInput(name, value);
        } else {
            instance[name] = value;
        }
    }
}
